import React, { Fragment, useEffect, useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { get, isNil } from 'lodash';
import { CheckCircle } from "lucide-react"

import Header from 'components/Header';
import { Grid, Column } from 'components/Layout';
import { Button } from 'components/Button';
import BankIcon from 'components/Icons/Bank';
import { nextAction } from 'actions/workflow';
import { Card, CardContent } from 'components/BankStatements/ui/card';

import style from './style.scss';

const BankVerificationCompleted = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const key = searchParams.get('key');
  const applicationId = useSelector(state => get(state, 'workflow.entityId', get(state, 'workflow.attributes.entityId', '')));
  const data = useSelector(state => get(state, 'workflow.data.data') || get(state, 'workflow.state.data'));

  useEffect(() => {
    if (!key) {
      navigate('/dashboard');
    } else if (isNil(data)) {
      navigate(`/applications/fsl/checkin?key=${key}`);
    }
  }, []);

  const handleSubmit = (isSubmitted) => {
    setIsLoading(true);
    dispatch(nextAction({
      url: `/workflows/fsl/${key}/next`,
      data: {
        isSubmitted,
      },
      success: (res) => {
        const nextUrl = get(res, 'data.url') || get(res, 'state.url');
        if (nextUrl) navigate(nextUrl);
        setIsLoading(false);
      },
      fail: (error) => {
        setIsLoading(false);
        console.log(error);
      },
    }));
  };

  return (
    <Fragment>
      <Header />
      <section className="container section">
        <div className={cn('use-tailwind grid-container fluid', style['bank-verification-completed-page'])}>
          <div className="grid-container">
            <Grid className="grid-margin-x max-limited">
              {applicationId ? (
                <div className="cell small-12" style={{ textAlign: 'left' }}>
                  <p className="mb-0 p-large"><b>Application ID:</b> {applicationId}</p>
                </div>
              ) : null}

              <Column className="small-12 large-10 large-offset-1 mt-8">
                <Card className="w-full shadow-lg">
                  <CardContent className="pt-5">
                    <div className="pt-6 pb-4">
                      <div className="flex flex-col items-center text-center space-y-4">
                        <p className="text-gray-600 max-w-md mx-auto text-sm md:text-base">
                          Great work! Your bank verification is complete. Just to make sure, did you include all of your income and
                          your monthly drafts to {get(data, 'dbaName')}?
                        </p>

                        <h2 className="text-lg md:text-xl font-bold">What would you like to do next?</h2>
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 px-6 pb-6">
                      <Button
                        onClick={() => handleSubmit(false)}
                        disabled={isLoading}
                        className="flex flex-1 flex-col items-center justify-between p-5 border-2 border-solid border-green-600 rounded-lg text-left hover:bg-green-50 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 h-[140px]"
                      >
                        <BankIcon className="h-10 w-10 text-gray-800" />
                        <h3 className="font-bold text-center text-base text-green-600">I need to report more income or monthly drafts</h3>
                      </Button>

                      <Button
                        onClick={() => handleSubmit(true)}
                        disabled={isLoading}
                        className="button flex flex-1 flex-col items-center p-5 bg-none bg-green-600 rounded-lg hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 h-[140px]"
                      >
                        <div className="flex flex-col items-center justify-center h-full space-y-3">
                          <CheckCircle className="h-10 w-10 text-white" />
                          <h3 className="text-center font-bold text-xl text-white">Submit</h3>
                        </div>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Column>

              <Column className="text-center small-12 large-10 large-offset-1 mt-4">
                <p className={style.helperText}>For Assistance call (949) 694-5001</p>
              </Column>
            </Grid>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default BankVerificationCompleted;
